/*  Shared  */
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { ProviderType } from 'application/Shared/types/tenantProvider';

/*  NPM  */
import axios from 'axios';

/*  Objects  */
import Session from 'objects/Session';

const cleanUpDomainsArray = (domains) =>
    Array.from(new Set(domains?.split(',').map((d) => d.trim())));

export const makeUpdateTenantPayload = (formValues) => {
    const { domains, updateDomains } = formValues;
    if (updateDomains) {
        return {
            domains: cleanUpDomainsArray(domains),
            partnerUpdate: true,
            setReprocessLogs: true
        };
    }
    return null;
};

export const makePayload = (formValues) => {
    const { azureTenantId, domains, name, providerType } = formValues;

    const domainsArray = cleanUpDomainsArray(domains);

    const payload = { domains: domainsArray, providerType, tenantName: name };

    if (azureTenantId) {
        payload.tenantId = azureTenantId;
    }

    const authData = makeProviderAuthData(formValues, providerType);

    if (Object.keys(authData).length > 0) {
        payload.authData = authData;
    }

    // Flag to kick off Workato workspace onboarding flow.
    payload.createWorkato = true;

    return payload;
};

const defaultOnSuccessFn = () => {};

export const makeProviderAuthData = (values, providerType) => {
    const authData = {};
    if (providerType === ProviderType.Microsoft) {
        const {
            azureApplicationId,
            azureApplicationSecret,
            useAsmAppCredentials
        } = values;
        if (useAsmAppCredentials) {
            authData.useAsmAppCredentials = true;
        } else {
            authData.applicationId = azureApplicationId;
            authData.applicationSecret = azureApplicationSecret;
        }
    }
    if (providerType === ProviderType.Google) {
        const { accessToken, refreshToken } = values;
        authData.accessToken = accessToken;
        authData.refreshToken = refreshToken;
    }
    return authData;
};

export const createClient = async ({
    onSuccess = defaultOnSuccessFn,
    setIsSubmitting = defaultOnSuccessFn,
    setLinkedTenantId = defaultOnSuccessFn,
    values,
    t,
    updateSession
}) => {
    setIsSubmitting(true);

    const payload = makePayload(values);

    debug.dev('Creating New Client', payload);

    try {
        const response = await axios.post('/clientLink', payload);
        if (!Response.hasData(response)) {
            debug.e(response);
            Notify.e(t('Provider.Shared.unable_create_client'));
            setIsSubmitting(false);
            return;
        }
        const data = Response.getData(response);

        // reload the session to update partnerTenantLinks so they will be shown in TenantSelector in the sidebar
        const userSession = await Session.getSessionInfo();
        updateSession(userSession);

        onSuccess(data.linkedTenantId);
        setLinkedTenantId(data.linkedTenantId);
        Notify.s(t('Provider.Shared.created_client'));
        setIsSubmitting(false);
    } catch (e) {
        debug.e(e);
        Notify.e(e.response?.data?.message || e.message);
        setIsSubmitting(false);
    }
};

export const getGWSAPI = (accessToken) =>
    axios.create({
        baseURL: 'https://admin.googleapis.com/admin/directory/v1',
        headers: {
            Authorization: `Bearer ${accessToken}`
        }
    });

export const excludeGroups = async ({
    clientId,
    groups,
    onboarding,
    providerInfo,
    providerRoute,
    t,
    useIntegrationWizard
}) => {
    let success = true;
    // Submit provider groups when displaying on a single page
    if (!useIntegrationWizard && !onboarding) {
        const promises = Object.values(groups).map(async (group) => {
            const { exists, id, isOrgUnit } = group;

            /* params */
            const params = {
                ...providerInfo,
                excluded: true,
                id,
                isOrgUnit,
                platformTenantId: clientId
            };

            try {
                // This case is a failsafe. The frontend doesn't display the checkbox
                // if the group is already in the database for the tenant.
                if (exists) {
                    // submit the group
                    return await axios.put(
                        `/${providerRoute}/tenantGroup/${id}`,
                        params
                    );
                }
                // create the group
                return await axios.post(
                    `/${providerRoute}/tenantGroup`,
                    params
                );
            } catch (error) {
                return error.response;
            }
        });

        try {
            const responses = await Promise.all(promises);
            success = responses.every((response) => response.status < 400);
            if (success) {
                await axios.post(`/provider/quickscan`, { clientId });
                if (Object.values(groups).length) {
                    Notify.s(
                        t(
                            'Provider.MicrosoftConfiguration.success_group_excluded_added'
                        )
                    );
                }
            } else {
                Notify.e(
                    t('Provider.MicrosoftConfiguration.error_saving_group')
                );
            }
        } catch (error) {
            Notify.e(t('Provider.MicrosoftConfiguration.error_saving_group'));
        }
    }
    return success;
};
